<!-- components/ChatRoom.vue -->
<template>
    <div class="chat-container">
      <!-- Login Screen -->
      <div v-if="!userEmail && !chatRoomSelected && !isAdmin" class="login-screen">
        <h2>Help Desk</h2>
        <input 
          v-model="emailInput" 
          type="email" 
          placeholder="Enter your email"
          @keyup.enter="login"
          class="email-input"
        >
        <button @click="login">Enter Chat</button>
      </div>
  
      <!-- Admin Chat Room Selector -->
      <div v-if="isAdmin && !chatRoomSelected" class="admin-chat-room-selector">
        
        <v-toolbar color="deep-purple accent-4">
          <v-toolbar-title>
            Select chat room
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <button @click="goback">Leave</button>
        </v-toolbar>
        <v-list>
        <v-list-item-group>
          <v-list-item v-for="user in users" :key="user.email">
            <v-list-item-content>
              <v-list-item-title>{{ user.email }}</v-list-item-title>
              <v-list-item-subtitle>{{ user.resolved ? 'Resolved' : 'Unresolved' }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn @click="selectChatRoom(user.email)">Enter Chat</v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list-item-group>
      </v-list>
      </div>
      <!-- Chat Room -->
      <div class="chat-room" v-if="chatRoomSelected">
        <div class="chat-header">
          <h3 v-if="isAdmin">Chat with {{ selectedChatRoomEmail }}</h3>
          <h3 v-else>Help Desk</h3>
          <!-- <button v-if="!isAdmin" @click="resolved">Resolved</button> -->
          <button @click="logout" class="leave-button">Leave</button>
        </div>
  
        <div class="messages-container" ref="messagesContainer">
          <div v-for="(message, index) in messages" :key="index" 
               :class="['message', message.email === userEmail ? 'sent' : 'received']">
            <div class="message-sender">{{ message.email }}</div>
            
            <!-- Text message -->
            <div v-if="message.type === 'text'" class="message-content">
              {{ message.content }}
            </div>
            
            <!-- Image message -->
            <img v-else-if="message.type === 'image'" 
                 :src="message.content" 
                 class="message-image">
            
            <!-- Video message -->
            <video v-else-if="message.type === 'video'" 
                   controls 
                   class="message-video">
              <source :src="message.content" type="video/mp4">
            </video>
          </div>
        </div>
  
        <div class="input-container">
          <input 
            v-model="newMessage" 
            @keyup.enter="sendMessage"
            placeholder="Type a message..."
          >
          
          <button @click="$refs.fileInput.click()"><v-icon>mdi-paperclip</v-icon></button>
          <button @click="sendMessage">Send</button>

          <!-- New file upload input -->
          <input 
            type="file" 
            ref="fileInput"
            @change="handleFileUpload" 
            accept="image/*,video/*" 
            class="file-input" 
            style="display: none;" 
          >
        </div>
      </div>
    </div>
  </template>
  
  <script>
  export default {
    data() {
      return {
        userEmail: null,
        emailInput: '',
        adminEmail: null,
        messages: [],
        newMessage: '',
        isAdmin: false,
        users: [], // Array to hold user data for admin view
        chatRoomSelected: false, // Flag to check if a chat room has been selected
        selectedChatRoomEmail: null, // Store the email of the selected chat room
        currentUserEmail: null // Store the current user email
      }
    },
  
    created() {
      // Check for existing user
      const savedEmail = localStorage.getItem('chatUserEmail')
      if (savedEmail) {
        this.userEmail = savedEmail
        this.isAdmin = this.checkIfAdmin(savedEmail)
        if (this.isAdmin) {
          this.adminEmail = savedEmail; // Store admin email if user is admin
          this.fetchUsers(); // Fetch users if admin
        }else{
          this.chatRoomSelected = true;
          this.fetchUserMessages(this.userEmail);
        }
      }
  
      // Load saved messages
      const savedMessages = localStorage.getItem('chatMessages')
      if (savedMessages) {
        this.messages = JSON.parse(savedMessages)
      }
    },
  
    methods: {
      login() {
        if (this.emailInput && this.validateEmail(this.emailInput)) {
          this.clearLocalData();
          this.userEmail = this.emailInput;
          this.isAdmin = this.checkIfAdmin(this.emailInput);
          localStorage.setItem('chatUserEmail', this.emailInput);
          this.emailInput = '';

          if (this.isAdmin) {
            this.adminEmail = this.userEmail; // Store admin email
            this.fetchUsers(); // Fetch users if admin
          }else{
            console.log("fetching user messages");
            this.chatRoomSelected = true;
            this.fetchUserMessages(this.userEmail);
          }
        } else {
          alert('Please enter a valid email');
        }
      },
  
      checkIfAdmin(email) {
        return email.endsWith('@ebmtech.com');
      },
  
      sendMessage() {
        if (this.newMessage.trim()) {
          const message = {
            id: this.generateMessageId(), // Unique ID for the message
            syncId: this.currentUserEmail, // Use the same sync ID for all messages in the conversation
            email: this.userEmail,
            type: 'text',
            content: this.newMessage,
            timestamp: new Date().toISOString()
          }
          this.messages.push(message)
          this.saveMessages()
          this.newMessage = ''
          this.scrollToBottom()
        }
      },
  
      handleFileUpload(event) {
        const file = event.target.files[0]
        if (!file) return

        const reader = new FileReader()
        const isImage = file.type.startsWith('image/')
  
        reader.onload = (e) => {
          const message = {
            id: this.generateMessageId(), // Unique ID for the message
            syncId: this.currentUserEmail, // Use the same sync ID for all messages in the conversation
            email: this.userEmail,
            type: isImage ? 'image' : 'video',
            content: e.target.result,
            timestamp: new Date().toISOString()
          }
          this.messages.push(message)
          this.saveMessages()
          this.scrollToBottom()
        }
  
        reader.readAsDataURL(file)
        event.target.value = '' // Reset file input
      },
  
      generateMessageId() {
        return 'msg-' + Date.now(); // Example: using timestamp for simplicity
      },
  
      saveMessages() {
        localStorage.setItem('chatMessages', JSON.stringify(this.messages))
      },
  
      scrollToBottom() {
        this.$nextTick(() => {
          const container = this.$refs.messagesContainer
          container.scrollTop = container.scrollHeight
        })
      },
  
      validateEmail(email) {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return re.test(email)
      },
      goback(){
        this.currentUserEmail = null; // Clear sync user on logout
        this.messages = []; // Clear all messages
        this.chatRoomSelected = false; // Reset chat room selection
        localStorage.removeItem('chatUserEmail'); // Clear user email from local storage
        localStorage.removeItem('chatMessages'); // Clear messages from local storage
        this.fetchUsers();
      },
      logout() {
        this.isAdmin = false;
        this.userEmail = null;
        this.adminEmail = null; // Clear admin email on logout
        this.currentUserEmail = null; // Clear sync user on logout
        this.messages = []; // Clear all messages
        this.chatRoomSelected = false; // Reset chat room selection
        localStorage.removeItem('chatUserEmail'); // Clear user email from local storage
        localStorage.removeItem('chatMessages'); // Clear messages from local storage
      },
  
      clearLocalData() {
        localStorage.removeItem('chatUserEmail');
        localStorage.removeItem('chatMessages');
      },
  
      fetchUsers() {
        // Simulate fetching user data (replace with actual API call)
        this.users = [
          { email: 'user1@example.com', resolved: false },
          { email: 'user2@example.com', resolved: true },
          // Add more users as needed
        ];
      },
  
      selectChatRoom(email) {
        this.selectedChatRoomEmail = email; // Store the selected chat room email
        this.chatRoomSelected = true; // Set the flag to true
        this.fetchMessages(email); // Fetch messages for the selected user
      },
  
      fetchMessages(email) {
        // Simulate fetching messages for the selected user (replace with actual API call)
        this.currentUserEmail = email; // Set the current user email
        this.messages = [
          { email: email, type: 'text', content: 'Hello from ' + email },
          { email: this.userEmail, type: 'text', content: 'Hi there!' },
          // Add more messages as needed
        ];
      },
  
      fetchUserMessages(email) {
        // Simulate fetching messages for the logged-in user (replace with actual API call)
        // This should be an API call to your backend to get messages for the user
        this.currentUserEmail = email; // Set the current user email
        // Example of fetched messages
        this.messages = [
          { email:'helpdesk@ebmtech.com', type: 'text', content: `Hello `+email+ `.Please write your queries and we will get back to you asap.`, timestamp: new Date().toISOString() },
          // Add more messages as needed
        ];
      },
      resolved(){
        console.log("resolved");
      }
    }
  }
  </script>
  
  <style scoped>
  .chat-container {
    max-width: 800px;
    margin: 0 auto;
    height: 100vh;
    display: flex;
    flex-direction: column;
  }
  
  .login-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 1rem;
  }
  
  .chat-room {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  
  .chat-header {
    padding: 1rem;
    background: #f5f5f5;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .admin-badge {
    background: #ff4444;
    color: white;
    padding: 0.2rem 0.5rem;
    border-radius:12px;
  }
  
  .messages-container {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  
  .message {
    max-width: 70%;
    padding: 0.5rem;
    border-radius: 8px;
  }
  
  .sent {
    align-self: flex-end;
    background: #007bff;
    color: white;
  }
  
  .received {
    align-self: flex-start;
    background: #e9ecef;
  }
  .mdi-paperclip {
    color: #fff !important;
}
  .message-sender {
    font-size: 0.8rem;
    margin-bottom: 0.2rem;
    opacity: 0.7;
  }
  
  .message-image {
    max-width: 100%;
    border-radius:12px;
  }
  
  .message-video {
    max-width: 100%;
    border-radius:12px;
  }
  
  .input-container {
    padding: 1rem;
    display: flex;
    gap: 0.5rem;
    background: #f5f5f5;
  }
  
  input {
    flex: 1;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius:12px;
  }
  
  button {
    padding: 0.5rem 1rem;
    background: #007bff;
    color: white;
    border: none;
    border-radius:12px;
    cursor: pointer;
  }
  
  button:hover {
    background: #0056b3;
  } 
  
  .login-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    background: #f5f5f5;
  }
  
  .email-input {
    width: 300px;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 25px;
    font-size: 14px;
    outline: none;
    transition: border-color 0.3s ease;
    text-align: center;
  }
  
  .email-input:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0,123,255,0.1);
  }
  
  .email-input::placeholder {
    color: #999;
  }
  
  /* Override only the email input */
  .login-screen input {
    flex: none;  /* Override flex only for login screen input */
    width: 300px;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 25px;
    font-size: 14px;
    outline: none;
    transition: border-color 0.3s ease;
    text-align: center;
  }
  
  .login-screen input:focus {
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0,123,255,0.1);
  }
  
  .login-screen input::placeholder {
    color: #999;
  }
  
  /* .admin-chat-room-selector {
    padding: 1rem;
  } */
  .leave-button{
    background: #c56161;
    color: white;
  }
  .leave-button:hover{
    background: #ca3232;
    color: white;
  }
  </style>
